import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {Asset} from '../../../models/asset.class';
import {RouteAssignmentManagerService} from '../../../../../data/routes/route-assignment-manager.service';
import {RouteAssignment} from '../../../../../shared/models/route-assignment';
import {ActionMenuItem} from '../../../../../shared/models/action-menu-item.class';
import {ShortDateOrTimePipe} from '../../../../../shared/formatting/short-date-or-time.pipe';
import {VehiclesManagerService} from '../../../../../data/vehicles/vehicles-manager.service';
import moment from 'moment/moment';
import { Router } from '@angular/router';
import { MainRoute, RootRoute } from '../../../../../shared/models/angular-routing';
import { InsightsRoute, ShiftDetailRoute } from '../../../../insights/insights-routing.module';
import { RouteAssignmentService } from '../../../../../data/routes/route-assignment.service';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { ToastService } from '../../../../../shared/services/toast.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActionMenuItemComponent } from '../../../../../shared/components/action-menu-item/action-menu-item.component';
import { LocationApiService } from '../../../../../data/location-api/location-api.service';

export enum QueueType {
  VEHICLES,
  ROUTES,
}

@Component({
  selector: 'app-route-assignment-queue',
  templateUrl: './route-assignment-queue.component.html',
  styleUrl: './route-assignment-queue.component.scss',
  imports: [MatProgressSpinner, CdkDropList, ActionMenuItemComponent, CdkDrag],
})
export class RouteAssignmentQueueComponent implements OnInit, OnChanges, OnDestroy {
  @Input() vehicle: Asset;
  @Input() routeConfigId: number;
  @Input() routeId: number;
  @Input() timeFromHoursFilter: number;

  allRecentRouteAssignments: RouteAssignment[];
  filteredAssignments: RouteAssignment[];
  items: ActionMenuItem[] = [];

  percentComplete = 0;
  percentCompleteIntervalId: NodeJS.Timeout;

  private readonly openSubscriptions = Array<Subscription>();

  constructor(
    private router: Router,
    private routeAssignmentManager: RouteAssignmentManagerService,
    private routeAssignmentService: RouteAssignmentService,
    private locationApi: LocationApiService,
    private vehicleManager: VehiclesManagerService,
    private shortDateOrTimePipe: ShortDateOrTimePipe,
    private toast: ToastService
  ) {}

  ngOnInit() {
    const assignmentsSubscription = this.routeAssignmentManager.recentRouteAssignments$.subscribe(assignmentsUpdate => {
      // console.log(assignmentsUpdate);
      this.allRecentRouteAssignments = assignmentsUpdate.state;
      this.reloadAssignments();
    });
    this.openSubscriptions.push(assignmentsSubscription);

    // update percent complete every 30s
    this.percentCompleteIntervalId = setInterval(() => {
      this.updatePercentComplete();
    }, 30 * 1000);
  }

  ngOnChanges(changes: SimpleChanges) {
    this.reloadAssignments();
  }

  ngOnDestroy() {
    this.openSubscriptions.forEach(subscription => subscription.unsubscribe());

    if (!!this.percentCompleteIntervalId) {
      clearInterval(this.percentCompleteIntervalId);
    }
  }

  private updatePercentComplete() {
    const queueType = !!this.vehicle ? QueueType.ROUTES : QueueType.VEHICLES;
    const inProgressAssignment = this.filteredAssignments?.find(a => a.onAssignmentFrom && !a.completed && !a.declined);
    if (inProgressAssignment) {
      this.locationApi.getRouteAssignmentTracking(inProgressAssignment.configId, inProgressAssignment.routeId).then(response => {
        const status = response.find(status => status.sessionkey === inProgressAssignment.shiftId);
        this.percentComplete = status?.percent_complete ?? 0;
        this.items = this.filteredAssignments.map(assignment => {
          return this.assignmentToItem(assignment, queueType);
        });
      });
    }
  }

  private reloadAssignments() {
    if (!!this.allRecentRouteAssignments) {
      const queueType = !!this.vehicle ? QueueType.ROUTES : QueueType.VEHICLES;
      this.filteredAssignments = RouteAssignment.sortByStatus(this.filterBySubject(this.allRecentRouteAssignments, queueType));
      this.updatePercentComplete();
      this.items = this.filteredAssignments.map(assignment => {
        return this.assignmentToItem(assignment, queueType);
      });
    }
  }

  private filterBySubject(assignments: RouteAssignment[], queueType: QueueType) {
    if (queueType === QueueType.ROUTES) {
      // assignments for the shift
      return assignments.filter(assignment => {
        return this.vehicle.shiftId ? assignment.shiftId === this.vehicle.shiftId : assignment.vehicleId === this.vehicle.id;
      });
    } else {
      return assignments.filter(assignment => {
        // assignments in last 24 hours for the route
        return (
          assignment.routeId === this.routeId &&
          assignment.configId === this.routeConfigId &&
          moment(assignment.created).isAfter(moment().subtract(24, 'hour'))
        );
      });
    }
  }

  private assignmentToItem(assignment: RouteAssignment, type: QueueType): ActionMenuItem {
    return new ActionMenuItem(
      assignment.id,
      type === QueueType.VEHICLES ? 'directions_car' : 'route',
      type === QueueType.VEHICLES ? this.getVehicleName(assignment.vehicleId) : assignment.routeName,
      this.getStatus(assignment),
      null,
      null,
      null,
      () => !!assignment.onAssignmentFrom && !assignment.completed,
      null,
      null,
      () =>
        !!assignment.onAssignmentFrom &&
        this.router.navigate(
          [
            `/${RootRoute.MAIN}`,
            MainRoute.INSIGHTS,
            InsightsRoute.SHIFT_DETAIL,
            assignment.shiftId,
            ShiftDetailRoute.ASSIGNMENT,
            assignment.id,
          ],
          {}
        ),
      null,
      null,
      []
    );
  }

  // enable only for vehicle queue
  drop(event: CdkDragDrop<ActionMenuItem[]>) {
    const completedLength = this.filteredAssignments.filter(assignment => assignment.completed).length;
    const allLength = this.filteredAssignments.length;
    if (completedLength === allLength) {
      // doing nothing, no items to re-order
      return;
    }
    const indexOfFirstIncomplete = completedLength;
    const indexOfFirstOrderable =
      this.filteredAssignments[completedLength].onAssignmentFrom && allLength > completedLength + 1 ? completedLength + 1 : completedLength;
    if (event.previousIndex < indexOfFirstOrderable || event.currentIndex < indexOfFirstOrderable) {
      // doing nothing, cannot touch completed assignments
      this.toast.short('Cannot re-order already completed or in-progress assignments.');
      return;
    }
    const routeAssignmentIds = this.items.map(item => +item.id);
    moveItemInArray(routeAssignmentIds, event.previousIndex, event.currentIndex);
    const incompleteAssignmentIds = routeAssignmentIds.slice(indexOfFirstIncomplete);
    this.routeAssignmentService.reOrderRouteAssignments(this.vehicle.id, this.vehicle.shiftId, incompleteAssignmentIds).catch(error => {
      console.error(error);
      this.toast.long(error);
    });
  }

  private getVehicleName(vehicleId: number): string {
    return this.vehicleManager.getVehicle(vehicleId)?.label;
  }

  private getStatus(assignment: RouteAssignment): string {
    if (!!assignment.completed && !assignment.cancelled) {
      return 'Completed ' + this.shortDateOrTimePipe.transform(new Date(assignment.completed));
    } else if (!!assignment.completed) {
      return 'Cancelled ' + this.shortDateOrTimePipe.transform(new Date(assignment.completed));
    } else if (assignment.declined) {
      return 'Declined ' + this.shortDateOrTimePipe.transform(new Date(assignment.declined));
    } else if (!!assignment.onAssignmentFrom) {
      return `In Progress ${(this.percentComplete * 100).toFixed(1)}%`;
    } else {
      return 'Assigned';
    }
  }
}
